import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import { ArrowLeft, LayoutDashboard, Clock, Users } from 'lucide-react';
import api from '../../services/api';

function formatDate(iso) {
    if (!iso) return '—';
    const utcStr = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    const d = new Date(utcStr);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
}

function formatDuration(secs, t) {
    if (!secs) return '—';
    if (secs < 60) return `${secs} ${t('history.sec')}`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m < 60) return `${m} ${t('history.min')} ${s > 0 ? s + ' ' + t('history.sec') : ''}`.trim();
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h} ${t('history.hour')} ${rm > 0 ? rm + ' ' + t('history.min') : ''}`.trim();
}

export default function SubcategoryHistoryPage() {
    const { t } = useTranslation();
    const { subId } = useParams();
    const navigate = useNavigate();
    const lang = i18n.language;

    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subName, setSubName] = useState('');

    useEffect(() => {
        api.get(`/admin/subcategories/${subId}`)
            .then(r => {
                setSubName(i18n.language === 'en' ? (r.data.name_en || r.data.name_ru || r.data.name_uz) : (lang === 'uz' ? (r.data.name_uz || r.data.name_ru) : r.data.name_ru));
            })
            .catch(() => { });
    }, [subId, lang]);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/activity/subcategory/${subId}?limit=200`)
            .then(r => setActivities(r.data))
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [subId]);

    // Stats
    const uniqueUsers = new Set(activities.map(a => a.user_name)).size;
    const totalViews = activities.length;
    const totalSeconds = activities.reduce((acc, a) => acc + (a.duration_seconds || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/admin/subcategories')}
                    className="p-2 rounded-lg bg-surface hover:bg-surface-2 border border-border text-muted hover:text-primary transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h2 className="text-2xl font-bold text-primary">
                        {subName || 'Dashboard'} — {t('history.user_title_suffix')}
                    </h2>
                    <p className="text-sm text-muted">
                        {t('history.subcategory_subtitle', "История просмотров дашборда")}
                    </p>
                </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { icon: Users, label: t('history.users', 'Пользователи'), value: uniqueUsers, color: 'text-brand' },
                    { icon: LayoutDashboard, label: t('history.dashboard_views'), value: totalViews, color: 'text-success' },
                    { icon: Clock, label: t('history.total_time'), value: formatDuration(totalSeconds, t), color: 'text-warning' },
                ].map(({ icon: Icon, label, value, color }) => (
                    <div key={label} className="bg-surface border border-border rounded-2xl p-5 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-surface-2 ${color}`}>
                            <Icon className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-xs text-muted font-semibold uppercase tracking-wider">{label}</p>
                            <p className={`text-2xl font-bold ${color}`}>{value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-sm text-left">
                    <thead className="bg-surface-2 border-b border-border text-muted text-xs uppercase">
                        <tr>
                            <th className="px-6 py-4 font-semibold">{t('history.col_user')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_duration')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_ip')}</th>
                            <th className="px-6 py-4 font-semibold">{t('history.col_date')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading ? (
                            <tr><td colSpan={4} className="px-6 py-10 text-center text-muted">{t('history.loading')}</td></tr>
                        ) : error ? (
                            <tr><td colSpan={4} className="px-6 py-10 text-center text-error">{error}</td></tr>
                        ) : activities.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-10 text-center text-muted">{t('history.not_found')}</td></tr>
                        ) : activities.map(a => (
                            <tr key={a.id} className="hover:bg-surface-2/50 transition-colors">
                                <td className="px-6 py-4 text-primary font-bold">
                                    {(i18n.language === 'en' ? (a.user_name_en || a.user_name_uz || a.user_name) : (lang === 'uz' ? (a.user_name_uz || a.user_name) : a.user_name)) || t('history.unknown_user')}
                                </td>
                                <td className="px-6 py-4 text-muted">
                                    {a.duration_seconds != null ? formatDuration(a.duration_seconds, t) : '—'}
                                </td>
                                <td className="px-6 py-4 font-mono text-xs text-muted">
                                    {a.ip_address || '—'}
                                </td>
                                <td className="px-6 py-4 text-muted whitespace-nowrap">
                                    {formatDate(a.created_at)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
